import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import CountUp from 'react-countup';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { FaArrowRight } from 'react-icons/fa';
import { projectsApi } from '../services/endpoints';
import { useTranslation } from 'react-i18next';

// Default projects if API fails or no projects — a static fallback, not
// derived from any prop/state, so it lives outside the component rather
// than being recreated (and re-triggering exhaustive-deps) every render.
const defaultProjects = [
    {
      id: '1',
      title: 'Luxury Villa - Dubai',
      description: 'Complete smart home automation with security, lighting, and climate control for a 5-bedroom villa.',
      category: 'Villa',
      image_url: 'https://images.unsplash.com/photo-1628744876497-eb30460be9f6?crop=entropy&cs=srgb&fm=webp&w=800&q=75&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzI2MzM4MDV8MA&ixlib=rb-4.1.0',
    },
    {
      id: '2',
      title: 'Smart Office Building',
      description: 'Enterprise automation system with access control, energy management, and intelligent lighting.',
      category: 'Building',
      image_url: 'https://images.unsplash.com/photo-1594873604892-b599f847e859?crop=entropy&cs=srgb&fm=webp&w=800&q=75&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzI2MzM4MDV8MA&ixlib=rb-4.1.0',
    },
    {
      id: '3',
      title: 'Presidential Palace',
      description: 'Bespoke smart solutions for a 50,000 sq ft palace with advanced security and automation.',
      category: 'Palace',
      image_url: 'https://images.unsplash.com/photo-1724582586529-62622e50c0b3?crop=entropy&cs=srgb&fm=webp&w=800&q=75&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzI2MzM4MDV8MA&ixlib=rb-4.1.0',
    },
    {
      id: '4',
      title: 'Government Building',
      description: 'Smart building management system with energy optimization and security integration.',
      category: 'Government',
      image_url: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?crop=entropy&cs=srgb&fm=webp&w=800&q=75&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzI2MzM4MDV8MA&ixlib=rb-4.1.0',
    },
    {
      id: '5',
      title: 'Modern Apartment Complex',
      description: 'Multi-unit smart home system with centralized management and individual controls.',
      category: 'Building',
      image_url: 'https://images.pexels.com/photos/3754595/pexels-photo-3754595.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    },
    {
      id: '6',
      title: 'Beachfront Villa',
      description: 'Coastal smart villa with automated blinds, lighting scenes, and entertainment systems.',
      category: 'Villa',
      image_url: 'https://images.pexels.com/photos/32913099/pexels-photo-32913099.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    },
];

const ProjectsSection = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const list = await projectsApi.getAll();
        if (list && list.length > 0) {
          setProjects(list);
        } else {
          setProjects(defaultProjects);
        }
      } catch (error) {
        console.log('Using default projects');
        setProjects(defaultProjects);
      }
    };

    fetchProjects();
  }, []);

  const [activeCategory, setActiveCategory] = useState('all');
  
  const categories = [
    { key: 'all', label: t('projects.all') },
    { key: 'villa', label: t('projects.villa') },
    { key: 'building', label: t('projects.building') },
    { key: 'palace', label: t('projects.palace') },
    { key: 'government', label: t('projects.government') }
  ];

  const filteredProjects =
    activeCategory === 'all'
      ? projects
      : projects.filter((p) => {
          const categoryMap = {
            'villa': 'Villa',
            'building': 'Building',
            'palace': 'Palace',
            'government': 'Government'
          };
          return p.category === categoryMap[activeCategory];
        });

  return (
    <section
      id="projects"
      ref={ref}
      className="section-padding bg-[#0a0e27] relative overflow-hidden"
      data-testid="projects-section"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-[#00d4ff]/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-12"
        >
          <div className="inline-block glass px-6 py-2 rounded-full mb-6">
            <span className="text-[#00d4ff] font-semibold uppercase tracking-wider text-sm">
              {t('projects.badge')}
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('projects.title')}
            <span className="gradient-text block mt-2">{t('projects.subtitle')}</span>
          </h2>
          <p className="text-white/70 text-lg max-w-3xl mx-auto mb-8">
            {t('projects.description')}
          </p>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category.key}
                onClick={() => setActiveCategory(category.key)}
                className={`px-6 py-2 rounded-full font-medium transition-all ${
                  activeCategory === category.key
                    ? 'bg-[#00d4ff] text-[#0a0e27]'
                    : 'glass text-white hover:bg-white/10'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Projects Slider */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.3 }}
        >
          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            spaceBetween={30}
            slidesPerView={1}
            navigation
            pagination={{ clickable: true }}
            autoplay={{ delay: 4000, disableOnInteraction: false }}
            breakpoints={{
              640: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
            className="!pb-12"
          >
            {filteredProjects.map((project) => (
              <SwiperSlide key={project.id}>
                <motion.div
                  whileHover={{ y: -10 }}
                  className="glass-strong rounded-3xl overflow-hidden h-full group cursor-pointer"
                >
                  {/* Image */}
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={project.image_url}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e27] via-transparent to-transparent" />

                    {/* Category Badge */}
                    <div className="absolute top-4 right-4 glass px-4 py-1 rounded-full">
                      <span className="text-[#00d4ff] text-sm font-semibold">
                        {project.category}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#00d4ff] transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-white/70 mb-4 leading-relaxed">
                      {project.description}
                    </p>

                    <button className="text-[#00d4ff] hover:text-white font-semibold flex items-center space-x-2 group/btn">
                      <span>{t('projects.viewDetails')}</span>
                      <FaArrowRight className="group-hover/btn:translate-x-2 transition-transform" />
                    </button>
                  </div>
                </motion.div>
              </SwiperSlide>
            ))}
          </Swiper>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16"
        >
          {[
            { value: 500, label: t('projects.stat1') },
            { value: 50, label: t('projects.stat2') },
            { value: 100, label: t('projects.stat3') },
            { value: 10, label: t('projects.stat4') },
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-4xl font-bold gradient-text mb-2">
                {isInView && <CountUp start={0} end={stat.value} duration={2.5} delay={index * 0.2} />}+
              </div>
              <div className="text-white/60">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ProjectsSection;